import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Check, X, Star, MessageSquare, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { BookingDetails } from '@/hooks/booking/useBookingDetails';
import { useAuth } from '@/contexts/AuthContext';
import { BookingCompletionDialog } from '@/components/booking/BookingCompletionDialog';
import { format } from 'date-fns';
import { sendCustomerNotification } from '@/data/services/customerNotifications';
import { sendVendorNotification } from '@/data/services/vendorNotifications';
import { sendAdminNotification } from '@/data/services/adminNotifications';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';

interface BookingActionsProps {
  booking: BookingDetails;
  onSuccess: () => void;
}

export const BookingActions = ({ booking, onSuccess }: BookingActionsProps) => {
  const { user, isAdmin } = useAuth();
  const { toast } = useToast();
  
  const [isLoading, setIsLoading] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  // Determine user role and permissions
  const currentUserId = user?.id;
  const isCustomer = currentUserId === booking.customer_id;
  const isVendor = currentUserId === booking.service?.owner_id;
  const canManage = isVendor || isAdmin;

  const handleAccept = async () => {
    if (!booking.id || !canManage) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('bookings')
        .update({ 
          status: 'accepted',
          updated_at: new Date().toISOString()
        })
        .eq('id', booking.id);

      if (error) throw error;

      toast({
        title: "Success ✅",
        description: "Booking has been accepted",
      });
      
      onSuccess();
    } catch (error) {
      console.error('Error accepting booking:', error);
      toast({
        title: "Error",
        description: "Failed to accept booking",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!booking.id || !canManage) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('bookings')
        .update({ 
          status: 'declined',
          decline_reason: declineReason || 'No reason provided',
          updated_at: new Date().toISOString()
        })
        .eq('id', booking.id);

      if (error) throw error;

      toast({
        title: "Booking Declined",
        description: "The booking has been declined",
      });
      
      setShowDeclineDialog(false);
      setDeclineReason('');
      onSuccess();
    } catch (error) {
      console.error('Error declining booking:', error);
      toast({
        title: "Error",
        description: "Failed to decline booking",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!booking.id || !isCustomer) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('bookings')
        .update({ 
          status: 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('id', booking.id);

      if (error) throw error;

      // Send notifications to all parties
      try {
        console.log('Sending cancellation notifications...');
        
        // Format date and time for notifications
        const bookingDate = booking.start_time ? 
          format(new Date(booking.start_time), 'EEEE, MMMM d, yyyy') : 'N/A';
        const bookingTime = booking.start_time ? 
          format(new Date(booking.start_time), 'h:mm a') : 'N/A';

        const notificationPromises = [];

        // 1. Notify customer
        if (booking.customer_email) {
          notificationPromises.push(
            sendCustomerNotification('booking', {
              customerName: booking.customer_name || 'Customer',
              customerEmail: booking.customer_email,
              serviceName: booking.service?.name || 'Service',
              date: bookingDate,
              time: bookingTime,
              location: booking.service?.location || booking.customer_address || null,
              note: booking.note,
              bookingId: booking.id,
              status: 'cancelled'
            })
          );
        }

        // 2. Notify vendor if service_id is available
        if (booking.service_id) {
          notificationPromises.push(
            sendVendorNotification('cancellation', booking.service_id, {
              customerName: booking.customer_name || 'Customer',
              customerEmail: booking.customer_email || '',
              dateTime: booking.start_time ? format(new Date(booking.start_time), 'yyyy-MM-dd HH:mm') : '',
              endTime: booking.end_time ? format(new Date(booking.end_time), 'HH:mm') : '',
              note: booking.note || '',
              serviceName: booking.service?.name || '',
              status: 'cancelled',
              bookingId: booking.id
            })
          );
        }

        // 3. Notify admin
        notificationPromises.push(
          sendAdminNotification('cancellation', {
            serviceName: booking.service?.name || 'Unknown Service',
            userName: booking.customer_name || 'Customer',
            userEmail: booking.customer_email || '',
            details: {
              date: bookingDate,
              time: bookingTime,
              bookingId: booking.id,
              location: booking.service?.location || booking.customer_address || null
            }
          })
        );

        // Execute all notifications
        await Promise.allSettled(notificationPromises);
        console.log('Cancellation notifications sent');
        
      } catch (notificationError) {
        console.error('Error sending cancellation notifications:', notificationError);
        // Don't fail the cancellation if notifications fail
      }

      toast({
        title: "Booking Cancelled",
        description: "Your booking has been cancelled and notifications have been sent",
      });
      
      setShowCancelDialog(false);
      onSuccess();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast({
        title: "Error",
        description: "Failed to cancel booking",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplete = async (completionData: any) => {
    if (!booking.id || !canManage) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('bookings')
        .update({ 
          status: 'completed',
          completion_notes: completionData.notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', booking.id);

      if (error) throw error;

      toast({
        title: "Success ✅",
        description: "Booking has been marked as completed",
      });
      
      setShowCompletionDialog(false);
      onSuccess();
    } catch (error) {
      console.error('Error completing booking:', error);
      toast({
        title: "Error",
        description: "Failed to complete booking",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderActions = () => {
    const status = booking.status?.toLowerCase();

    // Customer actions
    if (isCustomer) {
      switch (status) {
        case 'new':
        case 'pending':
        case 'accepted':
          return (
            <Button 
              variant="destructive" 
              onClick={() => setShowCancelDialog(true)}
              disabled={isLoading}
              className="w-full sm:w-auto"
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Cancel Booking
            </Button>
          );
        
        case 'completed':
          return (
            <Button 
              variant="outline" 
              className="w-full sm:w-auto"
              onClick={() => {
                // Navigate to reviews or show review dialog
                toast({
                  title: "Feature Coming Soon",
                  description: "Review functionality will be available soon",
                });
              }}
            >
              <Star className="h-4 w-4 mr-2" />
              Leave Review
            </Button>
          );
          
        default:
          return null;
      }
    }

    // Vendor/Admin actions
    if (canManage) {
      switch (status) {
        case 'new':
        case 'pending':
          return (
            <div className="flex flex-col sm:flex-row gap-2 w-full">
              <Button 
                onClick={handleAccept}
                disabled={isLoading}
                className="flex-1"
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                Accept
              </Button>
              <Button 
                variant="destructive"
                onClick={() => setShowDeclineDialog(true)}
                disabled={isLoading}
                className="flex-1"
              >
                <X className="h-4 w-4 mr-2" />
                Decline
              </Button>
            </div>
          );
          
        case 'accepted':
          return (
            <Button 
              onClick={() => setShowCompletionDialog(true)}
              disabled={isLoading}
              className="w-full sm:w-auto"
            >
              <Check className="h-4 w-4 mr-2" />
              Mark as Complete
            </Button>
          );
          
        default:
          return null;
      }
    }

    return null;
  };

  const actions = renderActions();
  if (!actions) return null;

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-2">
        {actions}
      </div>

      {/* Decline Dialog */}
      <AlertDialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Decline Booking</AlertDialogTitle>
            <AlertDialogDescription>
              Please provide a reason for declining this booking. This will be visible to the customer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-2">
            <Label htmlFor="decline-reason">Reason for declining</Label>
            <Textarea
              id="decline-reason"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Please provide a reason..."
              className="min-h-[100px]"
            />
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDecline}
              disabled={isLoading}
              className={cn(buttonVariants({ variant: "destructive" }))}
            >
              {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Decline Booking
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Booking</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this booking? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Booking</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCancel}
              disabled={isLoading}
              className={cn(buttonVariants({ variant: "destructive" }))}
            >
              {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Cancel Booking
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Completion Dialog */}
      {showCompletionDialog && (
        <BookingCompletionDialog
          isOpen={showCompletionDialog}
          onClose={() => setShowCompletionDialog(false)}
          onComplete={handleComplete}
          booking={booking}
        />
      )}
    </>
  );
};